import { useState, useMemo } from 'react';
import Layout from '@/components/Layout';
import { CollegeCard } from '@/components/Cards';
import { COLLEGES } from '@/lib/index';
import type { College } from '@/lib/index';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Search, GraduationCap, BookOpen, FlaskConical } from 'lucide-react';
import { motion } from 'framer-motion';

type Category = '全部' | '理学' | '工学' | '人文' | '社科' | '经管' | '医学';

const CATEGORIES: Category[] = ['全部', '理学', '工学', '人文', '社科', '经管', '医学'];

const categoryColors: Record<string, string> = {
  理学: 'bg-chart-2/10 text-chart-2 border-chart-2/20',
  工学: 'bg-chart-4/10 text-chart-4 border-chart-4/20',
  人文: 'bg-chart-1/10 text-chart-1 border-chart-1/20',
  社科: 'bg-chart-3/10 text-chart-3 border-chart-3/20',
  经管: 'bg-chart-5/10 text-chart-5 border-chart-5/20',
  医学: 'bg-destructive/10 text-destructive border-destructive/20',
};

const programsUndergrad = [
  { college: '化学学院', programs: ['化学', '应用化学', '化学生物学', '材料化学'] },
  { college: '数学科学学院', programs: ['数学与应用数学', '信息与计算科学', '统计学', '金融数学'] },
  { college: '物理科学学院', programs: ['物理学', '应用物理学', '天文学'] },
  { college: '生命科学学院', programs: ['生物科学', '生物技术', '生物信息学'] },
  { college: '经济学院', programs: ['经济学', '国际经济与贸易', '财政学', '金融学'] },
  { college: '商学院', programs: ['工商管理', '市场营销', '财务管理', '人力资源管理'] },
  { college: '历史学院', programs: ['历史学', '世界史', '考古学'] },
  { college: '文学院', programs: ['汉语言文学', '汉语国际教育', '古典文献学'] },
  { college: '法学院', programs: ['法学'] },
  { college: '环境科学与工程学院', programs: ['环境科学', '环境工程', '生态学'] },
  { college: '医学院', programs: ['临床医学', '预防医学', '医学影像学'] },
  { college: '信息技术科学学院', programs: ['计算机科学与技术', '软件工程', '人工智能', '数据科学与大数据技术'] },
];

const programsGrad = [
  { college: '化学学院', programs: ['有机化学', '无机化学', '物理化学', '分析化学', '高分子化学与物理'] },
  { college: '数学科学学院', programs: ['基础数学', '应用数学', '计算数学', '概率论与数理统计', '运筹学与控制论'] },
  { college: '物理科学学院', programs: ['凝聚态物理', '光学', '理论物理', '粒子物理与核物理'] },
  { college: '生命科学学院', programs: ['细胞生物学', '生物化学与分子生物学', '遗传学', '神经生物学'] },
  { college: '经济学院', programs: ['政治经济学', '西方经济学', '世界经济', '国民经济学', '区域经济学'] },
  { college: '商学院', programs: ['工商管理（MBA）', '企业管理', '市场营销', '财务管理', '人力资源管理'] },
  { college: '历史学院', programs: ['中国史', '世界史', '考古学', '文物与博物馆学'] },
  { college: '文学院', programs: ['中国语言文学', '比较文学与世界文学', '语言学及应用语言学'] },
  { college: '法学院', programs: ['法学（民商法）', '法学（国际法）', '法学（宪法与行政法）', '法学（刑法）'] },
  { college: '环境科学与工程学院', programs: ['环境科学', '环境工程', '大气科学', '生态学'] },
  { college: '医学院', programs: ['基础医学', '临床医学', '公共卫生与预防医学', '生物医学工程'] },
  { college: '信息技术科学学院', programs: ['计算机科学与技术', '软件工程', '人工智能', '信息安全', '数据科学'] },
];

export default function Academics() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<Category>('全部');

  const filteredColleges = useMemo(() => {
    return COLLEGES.filter((college: College) => {
      const matchesCategory = activeCategory === '全部' || college.category === activeCategory;
      const matchesSearch =
        !searchQuery ||
        college.name.includes(searchQuery) ||
        college.englishName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        college.disciplines.some((d) => d.includes(searchQuery)) ||
        college.description.includes(searchQuery);
      return matchesCategory && matchesSearch;
    });
  }, [searchQuery, activeCategory]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { 全部: COLLEGES.length };
    COLLEGES.forEach((c: College) => {
      counts[c.category] = (counts[c.category] || 0) + 1;
    });
    return counts;
  }, []);

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <section className="relative bg-sidebar overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(0deg, transparent, transparent 60px, rgba(255,255,255,0.05) 60px, rgba(255,255,255,0.05) 61px), repeating-linear-gradient(90deg, transparent, transparent 60px, rgba(255,255,255,0.05) 60px, rgba(255,255,255,0.05) 61px)',
              }}
            />
          </div>
          <div className="relative container mx-auto px-4 py-20">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-3xl"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-1 h-10 bg-chart-1 rounded-full" />
                <span className="text-sidebar-foreground/70 text-sm font-medium tracking-widest uppercase">
                  Nankai University
                </span>
              </div>
              <h1
                className="text-4xl md:text-5xl font-bold text-sidebar-foreground mb-4"
                style={{ fontFamily: "'Noto Serif SC', serif" }}
              >
                院系设置
              </h1>
              <p className="text-sidebar-foreground/70 text-lg leading-relaxed mb-8">
                南开大学设有文、理、工、经、管、法、医等学科门类，共26个学院，涵盖本科、硕士、博士完整培养体系。秉承
                <span className="text-chart-1 font-medium">「允公允能、日新月异」</span>
                的校训精神，培养德才兼备的卓越人才。
              </p>
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2 text-sidebar-foreground/80">
                  <GraduationCap className="w-5 h-5 text-chart-1" />
                  <span className="text-sm">26个学院</span>
                </div>
                <div className="flex items-center gap-2 text-sidebar-foreground/80">
                  <BookOpen className="w-5 h-5 text-chart-1" />
                  <span className="text-sm">100余个本科专业</span>
                </div>
                <div className="flex items-center gap-2 text-sidebar-foreground/80">
                  <FlaskConical className="w-5 h-5 text-chart-1" />
                  <span className="text-sm">8个国家重点学科</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="bg-card border-b border-border sticky top-16 z-30">
          <div className="container mx-auto px-4 py-4">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 border ${
                      activeCategory === cat
                        ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                        : 'bg-muted text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'
                    }`}
                  >
                    {cat}
                    <span
                      className={`ml-1.5 text-xs ${
                        activeCategory === cat ? 'text-primary-foreground/70' : 'text-muted-foreground'
                      }`}
                    >
                      {categoryCounts[cat] || 0}
                    </span>
                  </button>
                ))}
              </div>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="搜索学院、专业..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-muted border-border"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="container mx-auto px-4">
            {filteredColleges.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-20 text-muted-foreground"
              >
                <Search className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p className="text-lg">未找到相关学院</p>
                <p className="text-sm mt-1">请尝试其他关键词或分类</p>
              </motion.div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <p className="text-muted-foreground text-sm">
                    共找到
                    <span className="text-foreground font-semibold mx-1">{filteredColleges.length}</span>
                    个学院
                  </p>
                  {activeCategory !== '全部' && (
                    <Badge variant="outline" className={`text-xs border ${categoryColors[activeCategory] || ''}`}>
                      {activeCategory}
                    </Badge>
                  )}
                </div>
                <motion.div
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                  initial="hidden"
                  animate="visible"
                  variants={{
                    hidden: {},
                    visible: { transition: { staggerChildren: 0.07 } },
                  }}
                >
                  {filteredColleges.map((college: College) => (
                    <motion.div
                      key={college.id}
                      variants={{
                        hidden: { opacity: 0, y: 24 },
                        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
                      }}
                    >
                      <CollegeCard college={college} />
                    </motion.div>
                  ))}
                </motion.div>
              </>
            )}
          </div>
        </section>

        <section className="py-16 bg-muted/50">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="text-center mb-10"
            >
              <h2
                className="text-3xl font-bold text-foreground mb-3"
                style={{ fontFamily: "'Noto Serif SC', serif" }}
              >
                培养层次
              </h2>
              <p className="text-muted-foreground">涵盖本科、硕士、博士完整人才培养体系</p>
            </motion.div>

            <Tabs defaultValue="undergraduate" className="w-full">
              <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8">
                <TabsTrigger value="undergraduate">本科专业</TabsTrigger>
                <TabsTrigger value="graduate">研究生专业</TabsTrigger>
              </TabsList>

              <TabsContent value="undergraduate">
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
                >
                  {programsUndergrad.map((item, idx) => (
                    <div
                      key={idx}
                      className="bg-card border border-border rounded-lg p-5 hover:border-primary/40 hover:shadow-md transition-all duration-200"
                    >
                      <h3 className="font-semibold text-foreground mb-3 text-sm">{item.college}</h3>
                      <div className="flex flex-wrap gap-1.5">
                        {item.programs.map((prog, pIdx) => (
                          <span
                            key={pIdx}
                            className="text-xs px-2.5 py-1 bg-secondary text-secondary-foreground rounded-full"
                          >
                            {prog}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </motion.div>
              </TabsContent>

              <TabsContent value="graduate">
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
                >
                  {programsGrad.map((item, idx) => (
                    <div
                      key={idx}
                      className="bg-card border border-border rounded-lg p-5 hover:border-primary/40 hover:shadow-md transition-all duration-200"
                    >
                      <h3 className="font-semibold text-foreground mb-3 text-sm">{item.college}</h3>
                      <div className="flex flex-wrap gap-1.5">
                        {item.programs.map((prog, pIdx) => (
                          <span
                            key={pIdx}
                            className="text-xs px-2.5 py-1 bg-accent text-accent-foreground rounded-full"
                          >
                            {prog}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </motion.div>
              </TabsContent>
            </Tabs>
          </div>
        </section>

        <section className="py-16 bg-sidebar">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              {[
                { number: '8', label: '国家重点学科', desc: '教育部认定' },
                { number: '5', label: '国家重点实验室', desc: '世界一流水平' },
                { number: '26', label: '学院', desc: '覆盖主要学科门类' },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className="p-8"
                >
                  <div
                    className="text-5xl font-bold text-chart-1 mb-2 font-mono"
                  >
                    {item.number}
                  </div>
                  <div className="text-sidebar-foreground font-semibold text-lg mb-1">{item.label}</div>
                  <div className="text-sidebar-foreground/60 text-sm">{item.desc}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
